import React, { useState, useEffect } from 'react';
import { Box, Avatar } from '@mui/material';
import { deleteCredential, getAllCredentials, updateCredential } from '../../../services/apiservice';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import CredentialEditModel from './CredentialEditModel';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

function CredentialList() {
  const { data: fetchedCredentials, loading } = useApiData(() => getAllCredentials().then((res) => res.data), []);
  const [credentials, setCredentials] = useState([]);
  const [loadingCredentials, setLoadingCredentials] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modelOpen, setModalOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);

  useEffect(() => {
    if (fetchedCredentials) {
      setCredentials(fetchedCredentials);
    }
  }, [fetchedCredentials]);

  const handleEdit = (credential) => {
    setSelectedCredential(credential);
    setModalOpen(true);
  };

  const handleDelete = async (credential) => {
    const CredentialID = credential.CredentialID;
    try {
      setLoadingCredentials(prevState => ({ ...prevState, [CredentialID]: true }));
      const response = await deleteCredential(CredentialID);
      if (response.success) {
        setCredentials(credentials.filter(c => c.CredentialID !== CredentialID));
        setSnackbarMessage('Credential Deleted successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.message || 'Failed to delete Credential.');
        setSnackbarSeverity('error');
      }
    }
    catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setLoadingCredentials(prevState => ({ ...prevState, [CredentialID]: false }));
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCredential(null);
  };

  const handleSave = async (updatedCredential) => {
    try {
      const response = await updateCredential(updatedCredential);
      if (response.success) {
        setCredentials((prevCredentials) =>
          prevCredentials.map((credential) =>
            credential.CredentialID === updatedCredential.CredentialID
              ? { ...credential, ...updatedCredential }
              : credential
          )
        );
        setSnackbarMessage('Credential updated successfully!');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'CredentialID', label: 'Credential ID', width: 100 },
    { key: 'CredentialName', label: 'Credential Name', width: 200 },
    { key: 'CredentialDescription', label: 'Credential Description', width: 250 },
    { key: 'CredentialCategory', label: 'Credential Category', width: 150 },
    {
      key: 'logo',
      label: 'Credential Logo',
      width: 100,
      render: (credential) => (
        credential.CredentialImageURL ? (
          <Avatar src={credential.CredentialImageURL} sx={{ width: 40, height: 40 }} />
        ) : (
          <Avatar sx={{ width: 40, height: 40, backgroundColor: 'grey.300' }} />
        )
      ),
    },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Credentials List"
        columns={columns}
        rows={credentials}
        loading={loading}
        searchKeys={['CredentialName', 'CredentialDescription', 'CredentialCategory']}
        rowKey="CredentialID"
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleting={loadingCredentials}
      />
      <CredentialEditModel
        open={modelOpen}
        onClose={handleModalClose}
        credential={selectedCredential}
        onSave={handleSave}
      />
    </>
  );
}

export default CredentialList;
